// src/Components/Promo/PromoPanel.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

import Portfolio from './Portfolio';
import Newsfeed from './Newsfeed';
import UploadMailingList from './UploadMailingList';
import MailingList from './MailingList';
import UnsubscribeList from './UnsubscribeList';
import Broadcast from './Broadcast';

import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import AdminBg from '../../assets/images/AdminBackground.png';

const DARK_BLUE = '#00008B';

export default function PromoPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('PORTFOLIO');
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  const [showTabAlert, setShowTabAlert] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);

  // --- DRAG AND DROP STATE ---
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDraggingState, setIsDraggingState] = useState(false);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  const panelRef = useRef(null);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.focus({ preventScroll: true });
      }
    }, 100);

    return () => clearTimeout(focusTimer);
  }, []);

  // --- DRAG LOGIC ---
  const handlePointerDown = (e) => {
    if (e.target.closest('button')) return;

    isDraggingRef.current = true;
    setIsDraggingState(true);
    startPosRef.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!isDraggingRef.current) return;

      let newX = e.clientX - startPosRef.current.x;
      let newY = e.clientY - startPosRef.current.y;

      if (newY < -120) newY = -120;

      setOffset({
        x: newX,
        y: newY,
      });
    };

    const handlePointerUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDraggingState(false);
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const menuItems = [
    { id: 'PORTFOLIO', label: 'Portfolio' },
    { id: 'NEWSFEED', label: 'Newsfeed' },
    { id: 'UPLOAD_MAILING_LIST', label: 'Upload Mailing List' },
    { id: 'MAILING_LIST', label: 'Mailing List' },
    { id: 'BROADCAST', label: 'Broadcast' },
    { id: 'UNSUBSCRIBE', label: 'Unsubscribed / Bounces List' },
  ];

  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      setShowCloseAlert(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // --- ESCAPE KEY LOGIC ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === 'Escape' &&
        document.activeElement &&
        document.activeElement.tagName === 'INPUT'
      ) {
        return;
      }

      if (e.key === 'Escape' && !showCloseAlert && !showTabAlert) {
        e.preventDefault();
        e.stopPropagation();
        handleCloseAttempt();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [showCloseAlert, showTabAlert, handleCloseAttempt]);

  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;

    if (isDirty) {
      setPendingTab(newTab);
      setShowTabAlert(true);
    } else {
      setIsDirty(false);
      setActiveTab(newTab);
    }
  };

  const renderContent = () => {
    const commonProps = { setIsDirty };
    switch (activeTab) {
      case 'PORTFOLIO':
        return <Portfolio {...commonProps} />;
      case 'NEWSFEED':
        return <Newsfeed {...commonProps} />;
      case 'BROADCAST':
        return <Broadcast {...commonProps} />;
      case 'UPLOAD_MAILING_LIST':
        return <UploadMailingList {...commonProps} />;
      case 'MAILING_LIST':
        return <MailingList {...commonProps} />;
      case 'UNSUBSCRIBE':
        return <UnsubscribeList {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* 1. Static Backdrop Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,0.2)',
          zIndex: 100001,
          cursor: 'default',
        }}
      />

      {/* 2. Moveable Panel */}
      <Box
        ref={panelRef}
        tabIndex={-1}
        sx={{
          position: 'fixed',
          top: '120px',
          left: '50%',
          transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`,
          zIndex: 100002,
          outline: 'none',
        }}>
        <Paper
          elevation={0}
          sx={{
            width: '80vw',
            height: '80vh',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '8px 0px 12px -1px rgba(0,0,0,0.5)',
          }}>
          {/* HEADER (Drag Handle) */}
          <Box
            onPointerDown={handlePointerDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 0.5,
              minHeight: '40px',
              borderBottom: '1px solid black',
              backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: 'white',
              flexShrink: 0,
              cursor: isDraggingState ? 'grabbing' : 'grab',
            }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              sx={{
                fontSize: '1.2rem',
                color: 'black',
                pointerEvents: 'none',
              }}>
              Promo
            </Typography>
            <IconButton
              onClick={handleCloseAttempt}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label='close'
              size='small'
              sx={{ color: 'black', padding: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* MIDDLE SECTION */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            {/* SIDEBAR */}
            <Box sx={{ width: '150px', bgcolor: '#707070', flexShrink: 0, py: 0 }}>
              <List component='nav' disablePadding>
                {menuItems.map((item) => {
                  return (
                    <ListItemButton
                      key={item.id}
                      selected={activeTab === item.id}
                      onClick={() => handleTabChange(item.id)}
                      sx={{
                        color: 'white',
                        borderBottom: '1px solid white',
                        position: 'relative',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                        '&.Mui-selected': {
                          bgcolor: '#e3f2fd',
                          '& .MuiTypography-root': {
                            fontWeight: 600,
                            color: DARK_BLUE,
                          },
                          '&:hover': { bgcolor: '#bbdefb' },
                        },
                      }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: '0.9rem' }}>
                            {item.label}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            <Divider orientation='vertical' flexItem sx={{ borderColor: 'black' }} />

            {/* CONTENT */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                bgcolor: '#fff',
                display: 'flex',
                flexDirection: 'column',
              }}>
              {renderContent()}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* --- DIALOGS --- */}
      <ConfirmDialog
        open={showCloseAlert}
        onClose={() => setShowCloseAlert(false)}
        onConfirm={() => {
          setShowCloseAlert(false);
          onClose();
        }}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={showTabAlert}
        onClose={() => {
          setShowTabAlert(false);
          setPendingTab(null);
        }}
        onConfirm={() => {
          setShowTabAlert(false);
          setIsDirty(false);
          setActiveTab(pendingTab);
          setPendingTab(null);
        }}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </>
  );
}
